import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Ban, CheckCircle2, Shield, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// ────────────────────────────────────────────────────────────────────────────
// Permission matrix helpers
// The `permissions-catalog` endpoint returns MODULES (list) + LEVELS (["none",
// "r","rw"]) so adding a new admin module server-side auto-renders a row here.
// ────────────────────────────────────────────────────────────────────────────
function PermissionMatrix({ value, onChange, disabled }) {
  const [catalog, setCatalog] = useState({ modules: [], levels: ["none", "r", "rw"] });
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/permissions-catalog"); setCatalog(data); }
      catch (_e) { /* frontend degrades gracefully if catalog fails */ }
    })();
  }, []);
  const grouped = useMemo(() => {
    const g = {};
    (catalog.modules || []).forEach((m) => { (g[m.group] ||= []).push(m); });
    return g;
  }, [catalog]);
  const set = (key, level) => onChange({ ...value, [key]: level });
  const bulk = (level) => {
    const next = {};
    (catalog.modules || []).forEach((m) => { next[m.key] = level; });
    onChange(next);
  };
  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/40 space-y-3" data-testid="perm-matrix">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Module permissions
        </div>
        <div className="flex gap-1.5" data-testid="perm-bulk">
          <button type="button" onClick={() => bulk("none")} disabled={disabled}
                  className="text-[11px] px-2 py-0.5 rounded-full border border-slate-200 hover:bg-white disabled:opacity-40">
            Clear all
          </button>
          <button type="button" onClick={() => bulk("r")} disabled={disabled}
                  className="text-[11px] px-2 py-0.5 rounded-full border border-slate-200 hover:bg-white disabled:opacity-40">
            All read
          </button>
          <button type="button" onClick={() => bulk("rw")} disabled={disabled}
                  className="text-[11px] px-2 py-0.5 rounded-full border border-slate-200 hover:bg-white disabled:opacity-40">
            All read+write
          </button>
        </div>
      </div>
      {Object.keys(grouped).length === 0 && (
        <div className="text-slate-400 text-xs py-6 text-center">Loading modules…</div>
      )}
      {Object.entries(grouped).map(([group, mods]) => (
        <div key={group}>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1.5">{group}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {mods.map((m) => {
              const cur = (value?.[m.key] || "none");
              return (
                <div key={m.key} className="flex items-center justify-between gap-2 px-2 py-1 bg-white border border-slate-100 rounded-lg" data-testid={`perm-row-${m.key}`}>
                  <div className="text-sm text-slate-700 truncate">{m.label}</div>
                  <div className="flex gap-0.5 shrink-0" role="group" aria-label={`Permission for ${m.label}`}>
                    {["none", "r", "rw"].map((lvl) => (
                      <button
                        key={lvl}
                        type="button"
                        disabled={disabled}
                        onClick={() => set(m.key, lvl)}
                        data-testid={`perm-${m.key}-${lvl}`}
                        className={"text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase " + (
                          cur === lvl
                            ? (lvl === "rw" ? "bg-emerald-600 text-white" : lvl === "r" ? "bg-sky-600 text-white" : "bg-slate-300 text-slate-700")
                            : "text-slate-400 hover:bg-slate-100"
                        )}
                      >
                        {lvl === "none" ? "—" : lvl === "r" ? "Read" : "R+W"}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <div className="text-[11px] text-slate-400 leading-relaxed">
        <span className="font-semibold">Read</span> lets the user open the page and see data.
        <span className="font-semibold"> R+W</span> also allows create / update / delete.
        Role <span className="font-semibold text-emerald-700">Super admin</span> bypasses this matrix and has full access.
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Users list (customers OR admins)
// ────────────────────────────────────────────────────────────────────────────
export default function AdminUsersList({ role = "customer" }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editUid, setEditUid] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin", permissions: {} });

  const load = async () => { const { data } = await api.get(`/admin/users?role=${role}`); setRows(data); };
  useEffect(() => { load(); }, [role]); // eslint-disable-line

  const create = async () => {
    try {
      const body = { ...form, role: form.role || "admin" };
      // Restricted-admin without a single "rw" permission would be locked out,
      // so warn before we send it up.
      if (body.role === "restricted_admin" && !Object.values(body.permissions || {}).some((v) => v === "r" || v === "rw")) {
        toast.error("Restricted admin needs at least one module permission");
        return;
      }
      await api.post("/admin/users", body);
      toast.success("Admin created");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "admin", permissions: {} });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/admin/users/${editUid}/permissions`, {
        role: form.role,
        permissions: form.permissions,
      });
      toast.success("Permissions updated");
      setEditUid(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openEdit = (r) => {
    setEditUid(r.id);
    setForm({ name: r.name, email: r.email, password: "", role: r.role || "admin", permissions: r.permissions || {} });
  };
  const toggle = async (id) => { await api.post(`/admin/users/${id}/toggle-block`); load(); };

  const isAdminPage = role === "admin";
  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">{isAdminPage ? "Admin users" : "Customer users"}</h1>
          <p className="text-slate-500 mt-1">{isAdminPage ? "Staff with backoffice access. Grant per-module read / write permissions." : "End-customers who have signed up to book."}</p>
        </div>
        {isAdminPage && <Button data-testid="usr-new" onClick={() => { setForm({ name: "", email: "", password: "", role: "restricted_admin", permissions: {} }); setOpen(true); }} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full"><Plus className="w-4 h-4 mr-1" /> New admin</Button>}
      </div>

      <Card className="mt-5 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              {isAdminPage && <TableHead>Modules granted</TableHead>}
              <TableHead>Created</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(r => {
              const isSuper = r.role === "admin" || r.role === "superadmin";
              const grantedCount = r.permissions ? Object.values(r.permissions).filter((v) => v === "r" || v === "rw").length : 0;
              return (
                <TableRow key={r.id} data-testid={`usr-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>
                    <Badge className={(isSuper ? "bg-teal-100 text-teal-700" : "bg-sky-100 text-sky-700") + " border-0 capitalize"}>
                      {isSuper ? <><ShieldCheck className="w-3 h-3 mr-1 inline" /> Super admin</> : <><Shield className="w-3 h-3 mr-1 inline" /> Restricted</>}
                    </Badge>
                  </TableCell>
                  {isAdminPage && (
                    <TableCell className="text-xs text-slate-600">
                      {isSuper ? <span className="text-teal-700 font-semibold">Full access</span> : (grantedCount > 0 ? `${grantedCount} module${grantedCount === 1 ? "" : "s"}` : <span className="text-rose-500">None (locked out)</span>)}
                    </TableCell>
                  )}
                  <TableCell className="text-xs text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</TableCell>
                  <TableCell>{r.blocked ? <Badge className="bg-red-100 text-red-700 border-0">Blocked</Badge> : <Badge className="bg-emerald-100 text-emerald-700 border-0">Active</Badge>}</TableCell>
                  <TableCell className="whitespace-nowrap flex gap-1">
                    {isAdminPage && (
                      <Button size="sm" variant="outline" data-testid={`usr-edit-${r.id}`} onClick={() => openEdit(r)}>
                        <Shield className="w-3.5 h-3.5 mr-1" /> Permissions
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => toggle(r.id)} className={r.blocked ? "text-emerald-600" : "text-red-600"}>
                      {r.blocked ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Unblock</> : <><Ban className="w-3.5 h-3.5 mr-1" /> Block</>}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && <TableRow><TableCell colSpan={isAdminPage ? 7 : 6} className="text-center text-slate-400 py-10">No {isAdminPage ? "admin" : "customer"} users</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {/* Create admin modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl text-[var(--brand-accent)]">Create admin user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Name</Label><Input data-testid="usr-name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></div>
              <div><Label>Email</Label><Input data-testid="usr-email" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></div>
              <div><Label>Password</Label><Input data-testid="usr-pass" type="password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})}/></div>
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={v=>setForm({...form,role:v})}>
                  <SelectTrigger data-testid="usr-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Super admin (full access)</SelectItem>
                    <SelectItem value="restricted_admin">Restricted admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.role === "restricted_admin" && (
              <PermissionMatrix value={form.permissions || {}} onChange={(v) => setForm({ ...form, permissions: v })} />
            )}
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="usr-create" onClick={create} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit permissions modal */}
      <Dialog open={!!editUid} onOpenChange={(o) => !o && setEditUid(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl text-[var(--brand-accent)]">Edit permissions — {form.name} ({form.email})</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={v=>setForm({...form,role:v})}>
                <SelectTrigger data-testid="usr-edit-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Super admin (full access)</SelectItem>
                  <SelectItem value="restricted_admin">Restricted admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "restricted_admin" && (
              <PermissionMatrix value={form.permissions || {}} onChange={(v) => setForm({ ...form, permissions: v })} />
            )}
            {form.role === "admin" && (
              <div className="text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded-lg p-3">
                <ShieldCheck className="w-3.5 h-3.5 inline mr-1" /> Super admin — no per-module restrictions apply.
              </div>
            )}
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setEditUid(null)}>Cancel</Button><Button data-testid="usr-save-perms" onClick={saveEdit} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">Save permissions</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const AdminUsersAdmins = () => <AdminUsersList role="admin" />;
export const AdminUsersCustomers = () => <AdminUsersList role="customer" />;
